import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { acceptInvite, fetchInvitePreview, fetchMe } from '@/features/auth/auth-api';
import { setSession, useSession } from '@/features/auth/auth-store';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2, MailCheck } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/accept-invite/$token')({
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const session = useSession();
  const navigate = useNavigate();

  const preview = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => fetchInvitePreview(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: async () => {
      const result = await acceptInvite(token);
      const me = await fetchMe();
      setSession({
        user: me.user,
        activeOrg: me.activeOrg,
        accessToken: session?.accessToken ?? '',
        accessTokenExpiresAt: session?.accessTokenExpiresAt ?? 0,
      });
      return result;
    },
    onSuccess: () => {
      toast.success(t('auth.invite.acceptSuccess'));
      void navigate({ to: '/app' });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : t('auth.invite.acceptFailed');
      toast.error(message);
    },
  });

  return (
    <AuthLayout eyebrow={t('auth.invite.eyebrow', { defaultValue: 'You have an invite' })}>
      <div className="rounded-2xl border border-border/70 bg-card/95 p-8 shadow-elevate backdrop-blur">
        <div className="mb-5 flex items-center gap-3">
          <span className="inline-flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <MailCheck className="size-5" aria-hidden />
          </span>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{t('auth.invite.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {preview.isLoading ? t('auth.invite.loading') : null}
              {preview.isError ? t('auth.invite.invalid') : null}
              {preview.isSuccess
                ? t('auth.invite.invitedTo', { orgName: preview.data.invite.orgName })
                : null}
            </p>
          </div>
        </div>

        {preview.isSuccess ? (
          <dl className="space-y-2 rounded-lg border border-border/60 bg-muted/30 p-4 text-sm">
            <Row label={t('auth.invite.emailLabel')} value={preview.data.invite.email} />
            <Separator />
            <Row label={t('auth.invite.roleLabel')} value={preview.data.invite.role} capitalize />
            <Separator />
            <Row label={t('auth.invite.invitedBy')} value={preview.data.invite.invitedBy.email} />
          </dl>
        ) : null}

        {preview.isSuccess && !session ? (
          <Alert className="mt-5">
            <AlertTitle>{t('auth.invite.mustLoginTitle')}</AlertTitle>
            <AlertDescription>
              <Trans
                i18nKey="auth.invite.mustLoginBody"
                values={{ email: preview.data.invite.email }}
                components={{ strong: <span className="font-medium" /> }}
              />
            </AlertDescription>
          </Alert>
        ) : null}

        {session &&
        preview.isSuccess &&
        session.user.email.toLowerCase() !== preview.data.invite.email.toLowerCase() ? (
          <Alert variant="destructive" className="mt-5">
            <AlertDescription>
              {t('auth.invite.wrongAccount', {
                current: session.user.email,
                expected: preview.data.invite.email,
              })}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="mt-6 flex flex-col gap-2">
          {!session ? (
            <div className="flex w-full gap-2">
              <Button
                className="h-11 flex-1 bg-foreground text-background hover:opacity-95"
                onClick={() => navigate({ to: '/login' })}
              >
                {t('auth.invite.login')}
              </Button>
              <Button
                variant="outline"
                className="h-11 flex-1"
                onClick={() => navigate({ to: '/register' })}
              >
                {t('auth.invite.register')}
              </Button>
            </div>
          ) : (
            <Button
              className="h-11 w-full bg-foreground text-background hover:opacity-95"
              disabled={!preview.isSuccess || accept.isPending}
              onClick={() => accept.mutate()}
            >
              {accept.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              {t('auth.invite.accept')}
            </Button>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}

function Row({ label, value, capitalize }: { label: string; value: string; capitalize?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className={`text-sm font-medium ${capitalize ? 'capitalize' : ''}`}>{value}</dd>
    </div>
  );
}
